import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronRight } from "lucide-react";
import { format } from "date-fns";
import type { Recording } from "../../api/types";
import { formatDuration, parseUtcDate } from "../../utils";
import { getBrowserHash } from "../../utils/urlHash";
import { buildSessionTitle } from "../analytics/sessionTitle";
import SessionStatus from "./SessionStatus";
import SessionArchivedBadge from "./SessionArchivedBadge";
import SessionApps from "./SessionApps";
import { hasLocalActivityScope, LOCAL_ACTIVITY_SEARCH } from "./activityScope";

/** Captured seconds and the start–end label shown for a session; the end date only when it differs. */
export function sessionTiming(
  recording: Recording,
  language: string | undefined,
  separator = " · ",
) {
  const started = parseUtcDate(recording.date_started);
  const ended = parseUtcDate(recording.date_ended);
  const date = (value: Date) =>
    value.toLocaleDateString(language, { day: "numeric", month: "short", year: "numeric" });
  return {
    duration: Math.max(0, (ended.getTime() - started.getTime()) / 1000),
    range: `${date(started)}${separator}${format(started, "HH:mm")} – ${
      format(started, "yyyy-MM-dd") !== format(ended, "yyyy-MM-dd")
        ? `${date(ended)}${separator}`
        : ""
    }${format(ended, "HH:mm")}`,
  };
}

export default function RecentSession({
  recording,
  revision,
}: { recording: Recording; revision: number }) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { duration, range } = sessionTiming(recording, i18n.resolvedLanguage);
  const name = buildSessionTitle(undefined, recording.id, recording);
  return (
    <div className="relative flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-outline/70 px-4 py-3.5 last:border-0 hover:bg-info/5">
      <div className="min-w-0 basis-full @[800px]:flex-1 @[800px]:basis-0">
        <Link
          to={{
            pathname: `/recordings/${encodeURIComponent(recording.id)}/`,
            search: hasLocalActivityScope(location) ? LOCAL_ACTIVITY_SEARCH : "",
          }}
          state={{ returnHash: getBrowserHash() }}
          className="block font-medium text-text after:absolute after:inset-0 focus-visible:outline-info"
        >
          <span className="flex items-center gap-2">
            <span className="truncate" title={name}>
              {name}
            </span>
            {!recording.include_in_processes && <SessionArchivedBadge />}
          </span>
          <span className="mt-1 block text-xs font-normal text-text-dim">{range}</span>
        </Link>
      </div>
      <span className="w-20 shrink-0 whitespace-nowrap text-sm tabular-nums" title={t("duration")}>
        {formatDuration(duration)}
      </span>
      <SessionApps recording={recording} revision={revision} showProcessCount alwaysVisible />
      <span className="w-24 shrink-0">
        <SessionStatus recording={recording} />
      </span>
      <ChevronRight size={16} className="ml-auto shrink-0 text-text-dim" aria-hidden="true" />
    </div>
  );
}
